import { useCallback, useEffect, useRef, useState } from "react";
import axiosClient from "../services/api/axiosClient";

export interface AsyncJobState {
	jobId: string | null;
	status: "idle" | "pending" | "processing" | "completed" | "failed";
	progress?: number;
	downloadUrl?: string;
	error?: string;
}

interface UseAsyncJobOptions {
	pollingInterval?: number;
	maxAttempts?: number;
	onComplete?: (job: AsyncJobState) => void;
	onError?: (job: AsyncJobState) => void;
}

interface UseAsyncJobResult {
	job: AsyncJobState;
	start: (endpoint: string, payload?: Record<string, unknown>) => Promise<void>;
	cancel: () => void;
	reset: () => void;
}

const IDLE_STATE: AsyncJobState = { jobId: null, status: "idle" };

export function useAsyncJob(
	options: UseAsyncJobOptions = {},
): UseAsyncJobResult {
	const { pollingInterval = 2000, maxAttempts = 60 } = options;
	const onComplete = useRef(options.onComplete);
	const onError = useRef(options.onError);
	onComplete.current = options.onComplete;
	onError.current = options.onError;

	const [job, setJob] = useState<AsyncJobState>(IDLE_STATE);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const attemptsRef = useRef(0);
	const jobIdRef = useRef<string | null>(null);

	const stopPolling = useCallback(() => {
		if (intervalRef.current !== null) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		attemptsRef.current = 0;
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => stopPolling();
	}, [stopPolling]);

	const startPolling = useCallback(
		(jobId: string) => {
			stopPolling();
			attemptsRef.current = 0;

			intervalRef.current = setInterval(async () => {
				attemptsRef.current += 1;

				if (attemptsRef.current > maxAttempts) {
					stopPolling();
					const timedOut: AsyncJobState = {
						jobId,
						status: "failed",
						error: "Timed out waiting for job to complete",
					};
					setJob(timedOut);
					onError.current?.(timedOut);
					return;
				}

				try {
					const data = await (
						axiosClient as unknown as {
							get: (url: string) => Promise<{
								jobId: string;
								status: string;
								progress?: number;
								error?: string;
							}>;
						}
					).get(`/api/jobs/${jobId}`);

					const updated: AsyncJobState = {
						jobId,
						status: data.status as AsyncJobState["status"],
						progress: data.progress,
						error: data.error,
						downloadUrl:
							data.status === "completed"
								? `/api/jobs/${jobId}/download`
								: undefined,
					};

					setJob(updated);

					if (data.status === "completed") {
						stopPolling();
						onComplete.current?.(updated);
					} else if (data.status === "failed") {
						stopPolling();
						onError.current?.(updated);
					}
				} catch {
					// Network error — log and continue polling
					console.warn(`[useAsyncJob] Polling error for job ${jobId}`);
				}
			}, pollingInterval);
		},
		[pollingInterval, maxAttempts, stopPolling],
	);

	const start = useCallback(
		async (endpoint: string, payload: Record<string, unknown> = {}) => {
			stopPolling();

			try {
				const data = await (
					axiosClient as unknown as {
						post: (url: string, body: unknown) => Promise<{ jobId: string }>;
					}
				).post(endpoint, payload);

				const jobId = data?.jobId;

				if (!jobId || typeof jobId !== "string") {
					const failed: AsyncJobState = {
						jobId: null,
						status: "failed",
						error: "Server did not return a valid job ID",
					};
					setJob(failed);
					onError.current?.(failed);
					return;
				}

				jobIdRef.current = jobId;
				const pending: AsyncJobState = { jobId, status: "pending" };
				setJob(pending);
				startPolling(jobId);
			} catch (err) {
				const errMsg =
					err instanceof Error ? err.message : "Failed to start job";
				const failed: AsyncJobState = {
					jobId: null,
					status: "failed",
					error: errMsg,
				};
				setJob(failed);
				onError.current?.(failed);
			}
		},
		[startPolling, stopPolling],
	);

	const cancel = useCallback(() => {
		stopPolling();
		setJob(IDLE_STATE);
	}, [stopPolling]);

	const reset = useCallback(() => {
		stopPolling();
		jobIdRef.current = null;
		setJob(IDLE_STATE);
	}, [stopPolling]);

	return { job, start, cancel, reset };
}
